import { ClozeEditorResult } from '../models/scenario-cloze-editor.model';
import { ScenarioQuestion } from '../models/scenario.model';

/** Keep answers attached to their words when authors reorder the passage. */
export function loadClozeEditor(q: ScenarioQuestion): { text: string; distractors: string[] } {
  const terms = Array.isArray(q.terms) ? q.terms.map(term => typeof term === 'string' ? term : '') : [];
  const answers = Array.isArray(q.clozeAnswers) ? q.clozeAnswers : [];
  const used = new Set(answers);
  const text = (Array.isArray(q.clozeParts) ? q.clozeParts : []).map(p => typeof p === 'number' ? `[[${terms[answers[p]] || ''}]]` : p)
    .join(' ').replace(/\s+([,.;:!?])/g, '$1');
  return { text, distractors: terms.filter((_term, i) => !used.has(i)) };
}

export function buildClozeEditor(text: string, distractors: string[]): ClozeEditorResult {
  const clozeParts: (string | number)[] = [];
  const answers: string[] = [];
  const errors: string[] = [];
  const marker = /\[\[([^\[\]]*)\]\]/g;
  let end = 0;
  let match: RegExpExecArray | null;
  while ((match = marker.exec(text)) !== null) {
    const before = text.slice(end, match.index).trim();
    if (before) { clozeParts.push(before); }
    clozeParts.push(answers.length);
    answers.push(match[1].trim());
    end = marker.lastIndex;
  }
  const tail = text.slice(end).trim();
  if (tail) { clozeParts.push(tail); }
  if (/\[\[|\]\]/.test(text.replace(marker, ''))) { errors.push('Close every blank as [[answer]].'); }
  if (!answers.length) { errors.push('Add at least one blank to the passage.'); }
  answers.forEach((answer, i) => { if (!answer) { errors.push(`Write a correct answer for blank ${i + 1}.`); } });
  distractors.forEach((term, i) => { if (!term.trim()) { errors.push(`Write distractor ${i + 1}, or remove it.`); } });
  const allTerms = [...answers, ...distractors.map(term => term.trim())];
  const seen = new Set<string>();
  allTerms.filter(Boolean).forEach(term => {
    const key = term.toLocaleLowerCase();
    if (seen.has(key)) { errors.push(`“${term}” appears more than once. Each answer-bank term can fill only one blank.`); }
    seen.add(key);
  });
  const terms = allTerms.filter((term, i) => !!term && allTerms.findIndex(t => t.toLocaleLowerCase() === term.toLocaleLowerCase()) === i)
    .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true }));
  return { clozeParts, terms, clozeAnswers: answers.map(answer => terms.indexOf(answer)), answers, errors };
}

export function replaceClozeBlank(text: string, index: number, answer: string, remove = false): string {
  let current = -1;
  return text.replace(/\[\[([^\[\]]*)\]\]/g, (marker, inner: string) => {
    current++;
    return current === index ? (remove ? inner : `[[${answer}]]`) : marker;
  });
}

/** Validate the stored shape too, including questions edited through Advanced JSON. */
export function validClozeQuestion(q: ScenarioQuestion): boolean {
  if (!Array.isArray(q.clozeParts) || !Array.isArray(q.clozeAnswers) || !Array.isArray(q.terms)) { return false; }
  const parts = q.clozeParts || [];
  const answers = q.clozeAnswers || [];
  const terms = q.terms || [];
  const blanks = parts.filter(p => typeof p === 'number') as number[];
  return !!blanks.length && blanks.length === answers.length && new Set(blanks).size === blanks.length &&
    blanks.every(p => Number.isInteger(p) && p >= 0 && p < answers.length) &&
    answers.every(a => Number.isInteger(a) && a >= 0 && a < terms.length) && new Set(answers).size === answers.length &&
    terms.every(t => typeof t === 'string' && !!t.trim() && !/\[\[|\]\]/.test(t)) &&
    new Set(terms.map(t => t.trim().toLocaleLowerCase())).size === terms.length &&
    parts.every(p => typeof p === 'number' || typeof p === 'string');
}
